/* eslint-disable max-lines */
import { Row } from 'antd'
import List from 'app/components/Core/MachineGroup/List'
import KForm from 'app/components/Modules/Form/_KForm'
import KButton from 'app/components/Modules/General/KButton'
import KSteps from 'app/components/Modules/Navigation/KSteps'
import Alert from 'app/components/UI-Elements/Feedback/Alert'
import Portal from 'app/components/UI-Elements/General/Portal'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import { RecordType } from 'app/constants/GlobalTypes'
import {
  ChangeRequestNormalizer,
  MachineGroupNormalizer
} from 'app/lib/apollo/normalizers/LineItem'
import { ToolBox } from 'app/lib/hooks/toolBox'
import { useQuery } from 'app/lib/hooks/useQuery'
import Confirmation from 'app/pages/Rental/Components/MachineInteraction/Confirmation'
import MachineInteractionFooter from 'app/pages/Rental/Components/MachineInteraction/Footer'
import Form from 'app/pages/Rental/Components/MachineInteraction/Form'
import { RecordTypeEnum } from 'app/queries'
import * as rentals from 'app/queries/Rentals'
import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { useSelection } from '../hooks/useSelection'

type Props = {
  rental_id?: string
  setTitle?: (title: string) => void
  setSubtitle?: (subtitle: string) => void
  portalId?: string
  selectedIds?: string[]
}

export type InteractionType = 'extend' | 'vacant'

const MachineInteractionSteps: React.FC<Props> = ({
  rental_id,
  portalId,
  setTitle,
  setSubtitle,
  selectedIds
}) => {
  const { formatMessage } = useIntl()
  const { tRecordType } = ToolBox.useString()
  const [current, setCurrent] = useState(0)
  const { selectedItems, onCheck, resetSelection } = useSelection()
  const [interactionType, setInteractionType] = useState<InteractionType>('vacant')
  const { data, loading, refetch } = useQuery<{
    rental: IRental
    lineItems: ILineItem[]
    machineGroups: IMachineGroup[]
  }>(rentals.FETCH_RENTAL_PRODUCTS, {
    fetchPolicy: 'network-only',
    variables: {
      id: rental_id,
      lineItemIds: selectedIds,
      includeSubItems: !!selectedIds,
      lineItemType: ['product'],
      scopes: ['eligible_for_vacant_process']
    },
    normalizer: (data) => ({
      rental: {
        ...data.rental
      },
      lineItems: data.lineItems,
      machineGroups: MachineGroupNormalizer(data.lineItems)
    }),
    onCompleted: ({ lineItems }) => {
      if (!selectedIds) return

      onCheck(true, lineItems)
    }
  })

  const rental = data?.rental
  const machineGroups = data?.machineGroups || []

  const translate = (id: string) =>
    tRecordType(RecordType.machine_group, `machine_interaction.${id}`)

  useEffect(() => {
    if (interactionType === 'extend') {
      setTitle(translate('extend_machine'))
    } else if (interactionType === 'vacant') {
      setTitle(translate('vacate_machine'))
    } else {
      setTitle(null)
    }
  }, [interactionType])

  useEffect(() => {
    const projectName = rental?.project?.name
    if (projectName) {
      setSubtitle(
        formatMessage(
          { id: 'component.feedback.drawer.subtitle.line_item.update' },
          { projectName }
        )
      )
    }
  }, [rental])

  const allItemsVacant = useMemo(() => data?.lineItems?.every((product) => product.vacant), [data])

  const onNext = (interactionType: InteractionType) => {
    setInteractionType(interactionType)
    setCurrent(current + 1)
  }

  const onBack = () => {
    setInteractionType(null)
    resetSelection()
    setCurrent(current - 1)
  }

  const onStepClick = (step: number) => {
    if (step < current && !allItemsVacant) {
      setInteractionType(null)
      step === 0 && setCurrent(step)
    }
  }

  const getElements = () => {
    return [
      <List
        key="first-step"
        collection={machineGroups}
        props={{
          header: (
            <span className="font-weight-bold text-primary">
              {translate('extend_vacant_question')}
            </span>
          ),
          loading,
          visualRecordType: RecordType.line_item,
          displayHeader: true,
          showStatus: true,
          showCheckupDays: true,
          selectedItems,
          onCheck
        }}
      />,
      <Form key="second-step" selectedItems={selectedItems} interactionType={interactionType} />,
      <Confirmation
        key="third-step"
        multiple={selectedItems.length > 1}
        interactionType={interactionType}
        loading={loading}
        collection={machineGroups}
      />
    ]
  }

  const getFooter = () => (
    <Choose>
      <When condition={current === 2}>
        <Row gutter={[24, 0]}>
          <KCol span={24}>
            <KButton
              onClick={() => setCurrent(0)}
              recordType={RecordType.line_item}
              action="vacant_or_extend"
              type="secondary"
              size="large"
              block
            />
          </KCol>
        </Row>
      </When>
      <Otherwise>
        <MachineInteractionFooter
          onNext={onNext}
          onBack={onBack}
          step={current}
          selectedItems={selectedItems}
          disabled={!selectedItems.length}
          interactionType={interactionType}
        />
      </Otherwise>
    </Choose>
  )

  return (
    <Choose>
      <When condition={!(current === 0 && allItemsVacant)}>
        <KForm
          submitButtonProps={{
            hidden: true
          }}
          resetOnSuccess
          mutationOptions={{
            silentSuccess: true,
            payloadNormalizer: ChangeRequestNormalizer
          }}
          onSuccess={() => {
            onNext(interactionType)
            refetch()
            resetSelection()
          }}
          recordType={
            interactionType === 'extend'
              ? RecordTypeEnum.extend_line_item_request
              : RecordTypeEnum.line_item_vacant
          }
          layout="vertical">
          <KSteps
            stepTitleClassName="text-sm"
            recordType={RecordType.line_item}
            elements={getElements()}
            size="small"
            className=""
            direction="horizontal"
            current={current}
            onStepClick={onStepClick}
          />
          <If condition={!allItemsVacant}>
            <Portal id={portalId}>{getFooter()}</Portal>
          </If>
        </KForm>
      </When>
      <Otherwise>
        <Row gutter={[24, 0]}>
          <KCol span={24}>
            <Alert
              showIcon={true}
              type="default"
              message={translate('all_released.title')}
              description={translate('all_released.description')}
            />
          </KCol>
          <KCol span={24}>{getElements()[0]}</KCol>
        </Row>
      </Otherwise>
    </Choose>
  )
}

export default MachineInteractionSteps
